// src/pages/AgentsPage.tsx
//
// Agentes — /agents (Reforma UX §6.6, wireframe seção 07 "Agentes — Ativos").
// Lista em CARDS (padrão dos Negócios): avatar redondo (upload + menu de
// contexto), badges archetype + scope + status, switch ativar/desativar,
// selects provedor + modelo, ações Ver detalhes (MODAL) e Promover/Instanciar.
// Filtros: busca · archetype · scope · status. "Criar novo agente" = modal de
// escopo livre (CreateAssetModal). Detalhe vira MODAL (AgentDetailModal);
// rota /agents/:id mantida como deep-link que abre o modal sobre a lista
// (§10 item 2 — AgentDetailPage virou wrapper desta página).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type AgentListItem } from '../lib/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { Bot, ChevronLeft, ChevronRight, Plus, Globe, Copy, Search } from 'lucide-react';
import '@/styles/wf-agents.css';
import { CreateAssetModal } from '@/components/CreateAssetModal';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { buildPromoteFeedback, getPromoteCopy } from '@/lib/promote-feedback';
import { AgentCard } from '@/components/agents/AgentCard';
import { AgentDetailModal } from '@/components/agents/AgentDetailModal';
import { isAgentActive } from '@/components/agents/agent-constants';

// Filtros em <select> (wireframe §07 .dfilters) — valor '' = todos.
const ARCHETYPE_OPTIONS = [
  { value: '', label: 'Archetype: todos' },
  { value: 'companion', label: 'companion' },
  { value: 'domain_worker', label: 'domain_worker' },
  { value: 'system', label: 'system' },
  { value: 'ephemeral', label: 'ephemeral' },
];

const SCOPE_OPTIONS = [
  { value: '', label: 'Scope: todos' },
  { value: 'tenant', label: 'tenant' },
  { value: 'global', label: 'global' },
  { value: 'agent', label: 'agent' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Status: todos' },
  { value: 'active', label: 'ativo' },
  { value: 'inactive', label: 'inativo' },
];

// AGT-11 (admin+Ricco 2026-06-12): quantidade por página configurável —
// select 10/25/50/100 nos filtros (default 10, AGT-07).
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

export interface AgentsPageProps {
  /** Deep-link /agents/:id — abre o modal de detalhe sobre a lista. */
  detailRef?: string;
}

export function AgentsPage({ detailRef }: AgentsPageProps = {}) {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters (AGT: archetype · scope · status + busca) — selects do wireframe.
  const [search, setSearch] = useState('');
  const [archetypeFilter, setArchetypeFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Modal de detalhe (AGT-03). Deep-link inicializa do prop; abrir local não
  // muda a URL (mais simples); fechar quando veio de deep-link volta pra /agents.
  const [detailOpen, setDetailOpen] = useState(Boolean(detailRef));
  const [detailAgentRef, setDetailAgentRef] = useState<string | null>(detailRef ?? null);

  // Confirmação Promover / Instanciar (AGT-04)
  const [promoteTarget, setPromoteTarget] = useState<AgentListItem | null>(null);
  const [instantiateTarget, setInstantiateTarget] = useState<AgentListItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    api
      .listAgents()
      .then(setAgents)
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar agentes'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(load, []);

  const flashToast = (msg: string, ms = 6000) => {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  };

  const openDetails = (agent: AgentListItem) => {
    // AGT-09: ref por UUID — slug pode existir global E no tenant (dan-mall);
    // o UUID garante que a modal abre/edita exatamente a row do card clicado.
    setDetailAgentRef(agent.entity_id ?? agent.id);
    setDetailOpen(true);
  };

  const closeDetails = (open: boolean) => {
    setDetailOpen(open);
    if (!open && detailRef) {
      // Veio do deep-link /agents/:id — fechar volta pra lista canônica.
      navigate('/agents', { replace: true });
    }
  };

  const confirmPromote = async (agent: AgentListItem) => {
    // POST /v1/agents/:slug/promote — promove DIRETO via tool promote_asset.
    const result = await api.promoteAgent(agent.slug, 'global', true);
    flashToast(buildPromoteFeedback('agent', agent.name || agent.slug, result));
    load();
  };

  const confirmInstantiate = async (slug: string) => {
    // POST /v1/agents/:slug/install — clona global agent no tenant atual.
    const installed = await api.installAgent(slug);
    flashToast(`Agent ${slug} instanciado (slug local: ${installed.slug}).`);
    load();
  };

  // Atualização in-place de um card (avatar / status otimista do switch).
  const handleAgentUpdated = (updated: AgentListItem) => {
    setAgents((prev) =>
      prev.map((a) =>
        (a.entity_id ?? a.id) === (updated.entity_id ?? updated.id) ? { ...a, ...updated } : a,
      ),
    );
  };

  // Dedup: GET /v1/agents retorna união companion_profiles + agents — mesma
  // entidade pode aparecer 2x (espelhamento). 1 por entity_id, preferindo
  // entity_type='agent' (carrega allowed_tools persistidos).
  const deduped = useMemo(() => {
    const byKey = new Map<string, AgentListItem>();
    for (const a of agents) {
      if (a.status === 'archived') continue;
      const key = a.entity_id ?? a.id;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, a);
        continue;
      }
      if (existing.entity_type === 'companion_profile' && a.entity_type === 'agent') {
        byKey.set(key, a);
      }
    }
    // AGT-07 (admin 2026-06-11): ordem ALFABÉTICA por nome (fallback slug) —
    // o backend manda por created_at desc, o que embaralha a localização
    // visual dos agentes na grade.
    return Array.from(byKey.values()).sort((a, b) =>
      (a.name || a.slug).localeCompare(b.name || b.slug, 'pt-BR', { sensitivity: 'base' }),
    );
  }, [agents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deduped.filter((a) => {
      if (q && !`${a.name} ${a.slug}`.toLowerCase().includes(q)) return false;
      if (archetypeFilter && a.archetype !== archetypeFilter) return false;
      if (scopeFilter && a.scope !== scopeFilter) return false;
      if (statusFilter) {
        const group = isAgentActive(a.status) ? 'active' : 'inactive';
        if (group !== statusFilter) return false;
      }
      return true;
    });
  }, [deduped, search, archetypeFilter, scopeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const effectivePage = Math.min(Math.max(1, currentPage), totalPages);
  const paginated = useMemo(() => {
    const start = (effectivePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, effectivePage, pageSize]);

  return (
    <div>
      <PageHeader
        title="Agentes"
        description="Companions, Workers e Agentes de sistema do tenant."
        className="mb-2"
      />

      {/* Filtros: busca + archetype + scope + status + Criar (wireframe §07 .dfilters) */}
      <div className="dfilters">
        <div className="search" style={{ flex: 1, minWidth: 170 }}>
          <Search className="ic" aria-hidden />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Buscar agente"
            aria-label="Buscar agente"
          />
        </div>
        <select
          value={archetypeFilter}
          onChange={(e) => {
            setArchetypeFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filtro de archetype"
        >
          {ARCHETYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={scopeFilter}
          onChange={(e) => {
            setScopeFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filtro de scope"
        >
          {SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filtro de status"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {/* AGT-11: quantidade por página (pedido do Ricco) */}
        <select
          value={String(pageSize)}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          aria-label="Agentes por página"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n} por página</option>
          ))}
        </select>
        <button type="button" className="dnewbtn" onClick={() => setCreateOpen(true)}>
          <Plus className="ic" aria-hidden />
          Criar novo agente
        </button>
      </div>

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3 mb-4">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="agt-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-52 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Bot}
          title={deduped.length === 0 ? 'Nenhum agente cadastrado' : 'Nenhum resultado'}
          description={
            deduped.length === 0
              ? 'Crie Companions, Workers ou agentes de sistema para seu tenant.'
              : 'Tente limpar os filtros acima.'
          }
          action={
            deduped.length === 0
              ? { label: 'Criar novo agente', onClick: () => setCreateOpen(true) }
              : undefined
          }
        />
      ) : (
        <>
          <div className="agt-grid">
            {paginated.map((a) => (
              <AgentCard
                key={a.entity_id ?? a.id}
                agent={a}
                onViewDetails={openDetails}
                onPromote={setPromoteTarget}
                onInstantiate={setInstantiateTarget}
                onUpdated={handleAgentUpdated}
                onError={(msg) => flashToast(msg)}
                onNotice={(msg) => flashToast(msg)}
              />
            ))}
          </div>
          {filtered.length > pageSize && (
            <div className="flex items-center justify-between px-1 py-4">
              <p className="text-xs text-raios-muted">
                Página {effectivePage} de {totalPages} · {filtered.length}{' '}
                {filtered.length === 1 ? 'agente' : 'agentes'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={effectivePage === 1}
                  onClick={() => setCurrentPage(Math.max(1, effectivePage - 1))}
                  aria-label="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={effectivePage >= totalPages}
                  onClick={() => setCurrentPage(Math.min(totalPages, effectivePage + 1))}
                  aria-label="Próxima página"
                >
                  Próxima
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de detalhe (AGT-03) — também aberto via deep-link /agents/:id */}
      {detailAgentRef && (
        <AgentDetailModal
          open={detailOpen}
          onOpenChange={closeDetails}
          agentRef={detailAgentRef}
          onError={(msg) => flashToast(msg)}
        />
      )}

      {/* Confirmações Promover / Instanciar (AGT-04) */}
      {promoteTarget && (
        <ConfirmActionDialog
          open={!!promoteTarget}
          onOpenChange={(o) => {
            if (!o) setPromoteTarget(null);
          }}
          title={getPromoteCopy('agent').title}
          description={getPromoteCopy('agent').description}
          confirmLabel={getPromoteCopy('agent').confirmLabel}
          confirmVariant="brand"
          confirmIcon={Globe}
          onConfirm={() => confirmPromote(promoteTarget)}
        >
          <p className="text-sm text-raios-text-secondary">
            Agente: <span className="font-mono text-raios-text">{promoteTarget.slug}</span>
            {promoteTarget.name && ` — ${promoteTarget.name}`}
          </p>
        </ConfirmActionDialog>
      )}

      {instantiateTarget && (
        <ConfirmActionDialog
          open={!!instantiateTarget}
          onOpenChange={(o) => {
            if (!o) setInstantiateTarget(null);
          }}
          title="Instanciar agente no seu tenant?"
          description="Cria uma cópia deste agente global no seu tenant pra customização. A original continua disponível."
          confirmLabel="Instanciar"
          confirmVariant="brand"
          confirmIcon={Copy}
          onConfirm={() => confirmInstantiate(instantiateTarget.slug)}
        >
          <p className="text-sm text-raios-text-secondary">
            Agente: <span className="font-mono text-raios-text">{instantiateTarget.slug}</span>
            {instantiateTarget.name && ` — ${instantiateTarget.name}`}
          </p>
        </ConfirmActionDialog>
      )}

      {/* Toast inline simples (padrão SkillsPage) */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[500] max-w-md rounded-lg border border-raios-border bg-raios-elevated px-4 py-3 shadow-lg text-sm text-raios-text">
          {toast}
        </div>
      )}

      {/* Criar novo agente — modal de escopo livre (F8) */}
      <CreateAssetModal
        type="agent"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmitted={(msg) => flashToast(msg, 7000)}
      />
    </div>
  );
}
